import axios, { AxiosResponse, AxiosError } from 'axios'
import { BotMessages, BotButtons, UserMessage, BotMessage, AscMsgOptions } from './msgInterface'
import {
    iconMapping,
    keywordMapping,
    linkMapping,
    defaultUserInitial,
    defaultRelationship,
    defaultUserName,
    defaultCapitalisedUserInitial, axiosConfig
} from './utils/ascUtils'
import { FuzzySearch } from '../components/fuzzySearch'
import {widgetId} from "./utils/ascUtils";

type data = {
    plugin: string,
    endpoint_url: string,
    sender_id: string,
    initial_payload: string,
    auth_token: string,
    max_bot_response_delay_ms: number,
    testing: object | null,
}

type pageEntryButton = {
    id: string,
    text: string,
    iconReference: string,
    subtext: string,
    target: string,
    keyword: string,
    payload: string,
    title: string,
    btnType?: string,
}

type rasaCustom = {
    form_completed: boolean,
    url: string,

}

type ascSessionVariables = {
    inputName: string,
    inputInitial: string,
    inputCapitalisedInitial: string,
    inputRelationship: string,
    careProvider: string,
    localAuthority: string,
    letterCode: string | undefined,
    partnerOrganisation: string,
    eligibility: string,
    link: string
}

type ascButtonResponse = {
    content_type?: string,
    iconReference?: string,
    id?: string,
    name?: string,
    subtext?: string,
    target?: string,
    text?: string,
    payload?: string
    title?: string
}

type pageEntryFields = {
    id: string,
    buttons: Array<ascButtonResponse>,
    text: string,
    link: string,
    linkType: string,
    readMore: string,
    keywords: Array<string>,
    letterCode: string,
    shortText: string, // aka title
    custom?: rasaCustom,
    followUpId?: string
}

interface LaList {
    local_authorities: string[]
}

let userInputNextPayload = ''
let letterFillCompletionPayload = ''
let letterInput: Boolean = false
let currentKeyword: string | undefined = undefined
let inLetter = false

export default class AscPlugin {
    REQUIRED_DATA = []
    data: data

    constructor(data: data) {
        this.data = data
        this.init()
    }

    init() {
        // initialise or make changes to data if required and place in constructor.
        sessionStorage.setItem('notifSound', 'false')
    }

    validateData() {
        if (!this.data) throw new Error('No data provided.')
    }

    async getSenderId(): Promise<AxiosResponse<{ access_token: string }>> {
        let response = await axios.post(`${this.data.endpoint_url}/register_user?current_page=${window.location.href}`)
        return response.data.access_token
    }

    async apiCall(endpoint: string, jsonBodyData: any) {
        try {
            let response = await axios.post(endpoint, jsonBodyData, axiosConfig)

            // Returns ASC response
            if (jsonBodyData.msg_type === 'rasa') {
                return response.data
            }
            return response?.data.custom  // false if local_authority_name empty
        } catch (e) {
            const axiosErr = e as AxiosError
            const status = axiosErr.response ? axiosErr.response.status : 0
            // ACTIVATE FALLBACK MODE.
            if (status === 403) {
                console.error(`Received 403, chatbot disabled: ${e}`);
                let killswitch = document.querySelector(widgetId)
                killswitch?.remove();
                window.alert('Sorry, we had to temporarily turn off the chatbot.')
            } else {
                throw e;
            }
        }
    }

    async userInputResponse(sender_id: string, id: string, message?: string, messageType?: string) {
        const jsonBodyData: any ={
            message: message,
            payload: id,
            sender: sender_id,
            msg_type: messageType,
        }
        let keyword = await this.apiCall(`${this.data.endpoint_url}/webhook`, jsonBodyData)
        let localAuth
        if (keyword?.local_authority_name !== false && keyword?.local_authority_name !== undefined) {
            localAuth = keyword.local_authority_name
            sessionStorage.setItem('localAuthorityName', localAuth)
        }
    }

    async requestEmailTranscript(sender_id: string, email?: string) {
        let variables = this.getSessionVariables()
        let inputData = {
            user_name: variables.inputName,
            user_email: email,
            initial: variables.inputInitial,
            capitalisedInitial: variables.inputCapitalisedInitial,
            relationship: variables.inputRelationship,
            letter_code: variables.letterCode
        }
        let message = JSON.stringify(inputData)
        await this.userInputResponse(sender_id,
            "email",
             message,
            "emailinput"
        )
        currentKeyword = undefined
    }

    async letterFillResponse(sender_id: string, id: string, message?: string) {
        let localAuthority = null;
        let userInitial = defaultUserInitial;
        let capitalisedUserInitial = defaultCapitalisedUserInitial;
        let userRelationship = defaultRelationship;
        let userName = defaultUserName;
        let letterCode = null;
        if (sessionStorage['localAuthorityName']) {
          localAuthority = sessionStorage['localAuthorityName'];
        }
        if (sessionStorage['initial']) {
          userInitial = sessionStorage['initial'];
        }
        if (sessionStorage['capitalisedInitial']) {
            capitalisedUserInitial = sessionStorage['capitalisedInitial'];
        }
        if (sessionStorage['relationshipdescriptioninput']) {
          userRelationship = sessionStorage['relationshipdescriptioninput'];
        }
        if (sessionStorage['userinput']) {
          userName = sessionStorage['userinput'];
        }
        if (sessionStorage['letterCode']) {
          letterCode = sessionStorage['letterCode'];
        }

        const userData = {
            user_name: userName,
            initial_2: userInitial,
            relation_3: userRelationship,
            local_authority: localAuthority,
            letter_code: letterCode,
            initial_3: capitalisedUserInitial,
        }
        const jsonBodyData: any ={
            message: `/start_form_${letterCode}_template${JSON.stringify(userData)}`,
            payload: '',
            sender: sender_id,
            msg_type: 'rasa',
        }
        if (letterInput) {
            id?.startsWith('/') ? jsonBodyData.message = id : jsonBodyData.message = (message || "")
            jsonBodyData.payload = id
        }
        letterInput = true
        let response = await this.apiCall(`${this.data.endpoint_url}/webhook`, jsonBodyData)
        return response
    }


    async getEntry(sender_id: string, id: string, message?: string, msgType?) {
        let messageType: string
        currentKeyword ? messageType = keywordMapping[currentKeyword] : msgType === "contentful-button" ? messageType = msgType : messageType = 'contentful'
        let actualMessageType = "";
        msgType && currentKeyword !== 'emailinput' && currentKeyword !== 'letterfill' ? actualMessageType = msgType : actualMessageType = messageType
        const userInputSpecials = ['contentful', 'contentful-button', 'contentfului', 'contentfulse', 'contentfulstore', 'rasa', 'emailinput', 'rasaundo']

        if (msgType === 'rasaundo') {
            return await this.userInputResponse(sender_id, id, message, 'rasa')

        }

        if (!userInputSpecials.includes(actualMessageType)) {
            await this.userInputResponse(sender_id, id, message, actualMessageType)
        }
        if (messageType === 'letterfill') inLetter = true
        if (actualMessageType === 'letterfill') {
            return this.letterFillResponse(sender_id, id, message)
        }


        if (currentKeyword === 'emailinput' && message) {
            this.requestEmailTranscript(sender_id, message)
            message = undefined
        }

        if (messageType === 'contentfulstore' && currentKeyword) {
            if (currentKeyword === 'relationship-person-description' || currentKeyword === 'relationship-family-member') {
                // remove the 'My ' from the start of the button text. buttons are as: 'My mother' or similar.
                const snippedMessage = message?.replace('My ', '')
                sessionStorage.setItem('relationshipdescriptioninput', snippedMessage || "")
            }
            if (currentKeyword === 'careProvider') {
                sessionStorage.setItem('careProvider', message || "")
            }
            currentKeyword = undefined
        }

        if (actualMessageType === 'contentfulse' && currentKeyword) {
            let jsonStoreData: any ={
                message: message ?? 'error: message failed to save',
                payload: id,
                sender: sender_id,
                msg_type: currentKeyword,
            }
            if (currentKeyword === 'lasave') {
                jsonStoreData.message = sessionStorage.getItem('localAuthorityName')
                jsonStoreData.msg_type = 'save_local_authority'
            }
            this.sideEffects(jsonStoreData)
            currentKeyword = undefined
        }

        if (actualMessageType === 'contentfului' && currentKeyword) {
            if (currentKeyword === 'localAuthorityName' || currentKeyword === 'fuzzysearch') {
                sessionStorage.setItem('localAuthorityName', message || "")
            } else (
                sessionStorage.setItem(currentKeyword, message || "")
            )
            currentKeyword = undefined
        }

        if (currentKeyword && currentKeyword.includes('persona')) {
            let persona = '2' // note: default assumption is chatbot users are persona 2
            switch (currentKeyword) {
                case currentKeyword = 'personaone':
                    persona = '1'
                        break
                case currentKeyword = 'personatwo':
                    persona = '2'
                        break
                case currentKeyword = 'personathree':
                    persona = '3'
                        break
            }
            sessionStorage.setItem('persona', persona)
        }
        if (actualMessageType !== 'rasa' && msgType !== "contentful-button") inLetter = false
        const jsonBodyData: any ={
            message: message,
            payload: actualMessageType === 'rasa' ? "null" : id,
            sender: sender_id,
            msg_type: actualMessageType === 'rasa' ? 'rasa' : msgType === "contentful-button" ? 'contentful-button' : 'contentful',
        }
        // add to history if applicable -> add jsonBodyData
        return this.apiCall(`${this.data.endpoint_url}/webhook`, jsonBodyData)
    }

    goBack(send: Function) {
        // If we're in a letter filling situation, override back until we get to the start
        // then return to normal back behaviour
        if (inLetter) {
            send('undo', '/undo', {msgType: 'rasaundo'})
            return
        }

        userInputNextPayload = ''
        let historyArray = JSON.parse(sessionStorage.getItem('chatHistory') || "[]")
        historyArray.pop()
        while (historyArray[historyArray.length -1] === "null") {
            historyArray.pop()
        }
        const previousPagePayload = historyArray[historyArray.length - 1]
        sessionStorage.setItem('chatHistory', JSON.stringify(historyArray))
        if (previousPagePayload) send('back', previousPagePayload, {msgType: 'contentful', clearKeywords: true})
    }

    async getButtons(pageEntries: Array<pageEntryFields>, sender_id: string) {

        let buttonData: Array<any> = []
        let finalEntry = pageEntries.length - 1
        if (pageEntries[finalEntry]?.buttons) {
            let buttonIDs = pageEntries[finalEntry].buttons

            for (const buttonID of buttonIDs) {
                if (!buttonID.content_type) {
                    const {payload, title: text, } = buttonID
                    buttonData.push({target: payload, text, btnType: 'rasa', iconReference: 'rightarrow'})
                } else {
                    // Contentful button
                    buttonData.push({...buttonID, btnType: 'contentful-button'})
                }


            }

            // replace button iconReference with remote urls of actual assets

        }

        return buttonData
    }

    async fetchGenericLetter(): Promise<AxiosResponse<string>> {
        const letterCode = sessionStorage.getItem('letterCode')
        let response = await axios.get(`${this.data.endpoint_url}/default_letters/${letterCode}`)
        if (response.data.errors) {
            console.error(`Letter Code not found. Please check sessionStorage lettercode value is correct
            and corresponding letter link found in admin panel.`)
            response.data.link = ''
        }
        return response.data.link
    }

    async fetchLocalAuthsList(): Promise<string[]> {
        const response = await axios.get<LaList | {errors: string}>(`${this.data.endpoint_url}/local_authorities`)
        if ('errors' in response.data) {
            console.error(response.data.errors)
            return []
        }
        return response.data.local_authorities
    }

    async keywordConversions(keyword: string, button: any, i: number, sender_id: string): Promise<any> {
        let customField = {}
        if (keyword === 'urgency' || keyword === 'userexperience') {
            let color: string = ""
            switch (i) {
                case i = 0:
                    color = "#BA5757"
                        break
                case i = 1:
                    color = "#e6b800"
                        break
                case i = 2:
                    color = "#34B100"
                        break
            }
            customField = {
                keyword: keyword,
                color,
            }
            sessionStorage.removeItem('filledLetter') // userExperience is after letter fill flow.
        }

        if ((keyword === 'downloadtemplate' || 'download') &&
            sessionStorage.getItem('letterCode') &&
            button.iconReference === iconMapping.downloadtemplate) {
                const genericLetterTemplateUrl = await this.fetchGenericLetter()
                let filledLetterTemplateUrl: string | null = null
                if (sessionStorage.getItem('filledLetter')) {
                    filledLetterTemplateUrl = sessionStorage.getItem('filledLetter')
                }
                const downloadUrl = filledLetterTemplateUrl ?? genericLetterTemplateUrl
                customField = { downloadUrl, clearRasalines: true }

                const jsonBodyData: any ={
                    message: `/restart`,
                    payload: '',
                    sender: sender_id,
                    msg_type: 'rasa',
                }
                await this.apiCall(`${this.data.endpoint_url}/webhook`, jsonBodyData)
        }

        if (keyword === 'fuzzysearch') {
            let localAuthoritiesList = await this.fetchLocalAuthsList()
            customField = {
                customElement: FuzzySearch,
                localAuths: localAuthoritiesList,
            }
        }

        return customField
    }

    getSessionVariables(): ascSessionVariables {
        let inputName
        let inputInitial
        let inputCapitalisedInitial
        let inputRelationship
        let careProvider
        let localAuthority
        let letterCode
        let partnerOrganisation
        let eligibility
        let link
        sessionStorage.getItem('userinput') ? inputName = sessionStorage.getItem('userinput') : inputName = defaultUserName
        sessionStorage.getItem('initial') ? inputInitial = sessionStorage.getItem('initial') : inputInitial = defaultUserInitial
        sessionStorage.getItem('capitalisedInitial') ? inputCapitalisedInitial = sessionStorage.getItem('capitalisedInitial') : inputCapitalisedInitial = defaultCapitalisedUserInitial
        sessionStorage.getItem('relationshipdescriptioninput') ? inputRelationship = sessionStorage.getItem('relationshipdescriptioninput') : inputRelationship = defaultRelationship
        sessionStorage.getItem('careProvider') ? careProvider = sessionStorage.getItem('careProvider') : careProvider = '{insert supporting company here}'
        sessionStorage.getItem('localAuthorityName') ? localAuthority = sessionStorage.getItem('localAuthorityName') : localAuthority = 'not found'
        sessionStorage.getItem('letterCode') ? letterCode = sessionStorage.getItem('letterCode') : letterCode = undefined
        sessionStorage.getItem('partner_org') ? partnerOrganisation = sessionStorage.getItem('partner_org') : partnerOrganisation = 'us'
        sessionStorage.getItem('eligible_for_advice') ? eligibility = sessionStorage.getItem('eligible_for_advice') : eligibility = 'based in England'
        sessionStorage.getItem('here') ? link = sessionStorage.getItem('here') : link = '[here](https://advicelocal.uk/)'
        let variables: ascSessionVariables = {
            inputName,
            inputInitial,
            inputCapitalisedInitial,
            inputRelationship,
            careProvider,
            localAuthority,
            letterCode,
            partnerOrganisation,
            eligibility,
            link
        }
        return variables
    }

    formatTextContent(message) {
        let updatedMessage = message

        let variables = this.getSessionVariables()

        updatedMessage = updatedMessage.replace(/\$LocalAuthority/ig, `${variables.localAuthority}`)
        .replace(/\$UserName/ig, `${variables.inputName}`)
        .replace(/\$Initial/ig, `${variables.inputInitial}`)
        .replace(/\$capitalisedInitial/ig, `${variables.inputCapitalisedInitial}`)
        .replace(/\$relationship/ig, `${variables.inputRelationship}`)
        .replace(/\$careProvider/ig, `${variables.careProvider}`)
        .replace(/\$partner_org/ig , `${variables.partnerOrganisation}`)
        .replace(/\$eligible_for_advice/ig , `${variables.eligibility}`)
        .replace(/\$here/ig , `${variables.link}`)

        return updatedMessage
    }

    formatButton(text) {
        let updatedText = text

        let variables = this.getSessionVariables()

        updatedText = updatedText.replace(/\$partner_org/ig , `${variables.partnerOrganisation}`)
        .replace(/\$eligible_for_advice/ig , `${variables.eligibility}`)
        .replace(/\$here/ig , `${variables.link}`)

        return updatedText
    }

    async dataConverter(entries: Array<pageEntryFields>, buttons: Array<pageEntryButton>, sender_id: string, options: AscMsgOptions) {
        let response: BotMessages = []
        let responseButtons: BotButtons = []

        const userInputTypes = [
            'postcode',
            'userinput',
            'initial',
            'relationshipdescriptioninput',
            'familymemberinput',
            'ethnicityinput',
            'disabilityinput',
            'userfeedback',
            'rasainput',
            'emailinput'
        ]

        if (entries[entries.length - 1]?.custom?.form_completed) {
            inLetter = false
            let downloadUrl = entries[entries.length - 1]?.custom?.url ?? linkMapping[(sessionStorage.getItem('letterCode') || '')]
            sessionStorage.setItem('filledLetter', downloadUrl)
            entries.forEach(entry => {
                if (entry.text) {
                    let entryObj: BotMessage = {
                        id: entry.id,
                        title: 'Completing your letter',
                        message: entry.text,
                        custom: {
                            hideUserInput: true // hide user input true
                        },
                    }
                    response.push(entryObj)
                }
            })
            const letterCompletebuttons = [{
                text: 'Next',
                img: iconMapping['rightarrow'],
                payload: letterFillCompletionPayload, // obtained from button at start of flow
            }]
            response[response.length - 1].buttons = letterCompletebuttons
            letterInput = false
            currentKeyword = undefined
            userInputNextPayload = ''

            return response
        }

        // backend response
        if (entries) {
            entries.forEach(entry => {
                let entryObj: BotMessage = {
                    id: entry.id,
                    message: '',
                    custom: {
                        hideUserInput: true, // hide user input true
                        pageLayout: !options.isFollowup,
                        isFollowUp: options.isFollowup
                    }
                }
                // handle videos, readMore, standard text, etc.
                if (entry?.text?.includes('youtube')) {
                    const embedLink = entry.text.replace('watch?v=', 'embed/')
                    const embedLinkArr = embedLink.replace(').', '').split('(') // handle inline links such as those in letter autocomplete
                    embedLinkArr[1] ? entryObj.video = embedLinkArr[1] : entryObj.video = embedLink
                } else if (entry?.readMore){
                    const readMoreText = `${entry.text}$$$${entry.readMore}`
                    entryObj.message = readMoreText
                } else { // standard text
                    entryObj.message = entry.text
                }

                // handle title
                entryObj.title = 'Completing your letter'
                if (entry.shortText) {
                    entryObj.title = entry.shortText
                }

                // handle quicklinks
                if (entry.linkType) {
                    entryObj.quicklink = {
                        text: entry.linkType,
                        URL: entry.link,
                    }
                }

                // handle letterCodes
                if (entry.letterCode) {
                    sessionStorage.setItem('letterCode', entry.letterCode)
                }

                if (currentKeyword && currentKeyword.includes('persona')) {
                    let persona // note: default assumption is chatbot users are persona 2
                    sessionStorage.getItem('persona') ? persona = sessionStorage.getItem('persona') : persona = '2'
                    const sideEffectData = {
                        sender: sender_id,
                        message: `/set_persona{"persona": "${persona}"}`,
                        payload: "content",
                        msg_type: 'rasa'
                    }
                    this.sideEffects(sideEffectData)
                    entryObj.custom = {
                        hideUserInput: false,
                        pageLayout: false
                    }
                }

                // handle keywords
                if (entry.keywords?.length) {
                    currentKeyword = entry.keywords[0]
                    entryObj.custom = {
                        keyword: entry.keywords[0],
                        hideUserInput: true,
                        pageLayout: !options.isFollowup
                    }
                    if (userInputTypes.includes(entry.keywords[0])) {
                        entryObj.custom.hideUserInput = false // hide user input false
                    }

                } else if (letterInput) {
                    currentKeyword = 'letterfill'
                    entryObj.custom = {
                        keyword: 'letterfill',
                        hideUserInput: false,
                        pageLayout: false
                    }
                } else {
                    currentKeyword = undefined
                }

                if (entryObj.message) {
                let formattedContent = this.formatTextContent(entryObj.message)
                entryObj.message = formattedContent
                }
                entryObj.followUpId = entry.followUpId

                response.push(entryObj)
            })
        }

        const finalEntry = entries[entries.length-1]
        const entryKeyword = finalEntry?.keywords?.[0]
        // add in buttons
        // note keywords are an array, but currently we only use one keyword.
        // leaving as array for potential future change in functionality
        if (buttons && !userInputTypes.includes(entryKeyword)) {
            for (const button of buttons) {
                const buttonText = (button.text ? this.formatButton(button.text) : null)
                const buttonTitle = (button.title ? this.formatButton(button.title) : null)
                const buttonSubtext = (button.subtext ? this.formatButton(button.subtext) : null)
                const customButtonField = await this.keywordConversions(entryKeyword, button, buttons.indexOf(button), sender_id)
                responseButtons.push({
                    text: buttonText ?? buttonTitle,
                    subtext: buttonSubtext,
                    img: button.iconReference,
                    payload: button.target ?? button.payload,
                    msgType: button.btnType,
                    custom: customButtonField
                })
            }
            response[response.length - 1].buttons = responseButtons
            if (entryKeyword === 'letterfill' && buttons) {
                letterFillCompletionPayload = buttons[0].target ?? ''
            }
            if (currentKeyword === 'rasainput' && responseButtons.length > 1) {
                // prevents user input when a topic overview rasa response is generates
                response[response.length - 1].custom = {
                    hideUserInput: true,
                    pageLayout: false
                }
            }
        } else if (buttons && buttons.length > 0){
            // if userInput style page, there is only 1 button for 'next page'
            userInputNextPayload = buttons[0].target ?? undefined
        }

        return response
    }

    updateChatHistory(inputPayload) {
        let history = JSON.parse(sessionStorage.getItem('chatHistory') || "[]")
        if (!inputPayload.startsWith('/')) {
            if (!(history.indexOf(inputPayload) > -1))
                history.push(inputPayload)

            sessionStorage.setItem('chatHistory', JSON.stringify(history))
        }
    }

    async processInput(input: UserMessage) {
        if (input.message === 'undo') {
            // still send message to server
            await this.getEntry(input.sender_id, "null", '/undo', 'rasaundo')
            return []
        }

        let pageEntries: Array<any> = []
        // obtain initial entry from button payload
        let inputPayload

        const {msg_type, clearKeywords, clearNextPayload} = input.options || {}
        // Handle any special requests from the menu
        if (msg_type === "transcript_request") {
            return this.requestEmailTranscript(input.sender_id, input.payload)
        }
        if (msg_type === "user_experience") {
            return this.sideEffects({
                sender: input.sender_id,
                message: input.payload,
                msg_type: 'userexperience'
            })
        }
        if (msg_type === "user_feedback") {
            return this.sideEffects({
                sender: input.sender_id,
                message: input.payload,
                msg_type: 'userfeedback'
            })
        }
        if (msg_type && (msg_type.startsWith("equalities") || msg_type.startsWith("ethnicity") || msg_type.startsWith("armedservices"))) {
            return this.sideEffects({
                sender: input.sender_id,
                message: input.message,
                msg_type
            })
        }
        if (clearKeywords) {
            currentKeyword = undefined
        }

        if (clearNextPayload) {
            userInputNextPayload = ''
        }

        if (input.payload?.length === 0) {
            if(!currentKeyword && input.message) {
                inputPayload = input.message
                input.message = undefined
            }
        }

        if (input?.payload) {
            inputPayload = input.payload
        }

        if (userInputNextPayload !== '') {
            inputPayload = userInputNextPayload
        }

        if (msg_type === 'rasa') {
            inputPayload = "null"
            input.message = input.payload ? input.payload : ""
        }

        // for dev setup if custom firstPayload used
        const alternateFirstPayload = sessionStorage.getItem('firstPayload')
        if (alternateFirstPayload) {
            inputPayload = alternateFirstPayload
            sessionStorage.removeItem('firstPayload')
        }
        let actionServerResponse = await this.getEntry(input.sender_id, inputPayload, input.message, msg_type)
        if (actionServerResponse && actionServerResponse[0]?.custom?.payload_id) {
            inputPayload = actionServerResponse[0]?.custom?.payload_id.split("$")[1]
            currentKeyword = undefined;
            actionServerResponse = await this.getEntry(input.sender_id, inputPayload, actionServerResponse.message)
        }
        if (Array.isArray(actionServerResponse)) {
            pageEntries.push(...actionServerResponse)
        } else {
            if (!input.options?.isFollowup)
                this.updateChatHistory(inputPayload)
            pageEntries.push(actionServerResponse)
        }
        userInputNextPayload = ''


        let buttonData
        if (letterInput) {
            buttonData = pageEntries[pageEntries.length - 1].buttons
        } else {
            // obtain the buttons for the final entry on page
            buttonData = await this.getButtons(pageEntries, input.sender_id)
        }
        buttonData.forEach((button) => {
            button.iconReference = iconMapping[button.iconReference]
        })

        // use data converter to convert responses into relevant botMessages shape
        let botMessages = await this.dataConverter(pageEntries, buttonData, input.sender_id, input.options || {})
        return botMessages
    }

    sideEffects(jsonBodyData: any) {
        this.apiCall(`${this.data.endpoint_url}/webhook`, jsonBodyData)
    }
}
